import React, { useState, useEffect } from 'react';
import { markAttendance, getMyStatus } from './attendanceApi';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Clock, LogIn, LogOut, Activity, AlertCircle, ArrowLeft, LogOut as LogoutIcon, Calendar, Radio } from 'lucide-react';
import useAuthStore from '../../store/useAuthStore';
import { useNavigate } from 'react-router-dom';

const MarkAttendance = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [status, setStatus] = useState('out');
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [message, setMessage] = useState(null);

  const isLocutor = user?.role === 'locutor';
  const isGestor = user?.role === 'gestor';

  useEffect(() => {
    fetchStatus();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchStatus = async () => {
    const res = await getMyStatus();
    setStatus(res.status);
  };

  const handleMark = async (type) => {
    setLoading(true);
    try {
      await markAttendance(type);
      setMessage({ text: `¡${type === 'in' ? 'Entrada' : 'Salida'} registrada!`, type: 'success' });
      fetchStatus();
      // Si el locutor marca entrada, redirigir automáticamente al panel de operaciones
      if (type === 'in' && isLocutor) {
        setTimeout(() => navigate('/transport/dispatch'), 800);
      } else if (type === 'in' && isGestor) {
        // El gestor va al dashboard principal después de marcar
        setTimeout(() => navigate('/'), 800);
      } else {
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ text: "Error al registrar.", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 min-h-[60vh]">
      <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl p-10 border border-slate-200 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-600 to-indigo-600"></div>

        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-50 rounded-3xl flex items-center justify-center text-blue-600 shadow-sm">
              <Clock size={32} />
            </div>
          </div>
          <h1 className="text-3xl font-black text-slate-800 tracking-tighter uppercase mb-4">Reloj Control</h1>
          <div className="text-6xl font-mono font-black text-blue-700 drop-shadow-sm">
            {format(currentTime, 'HH:mm:ss')}
          </div>
          <p className="text-slate-500 mt-2 font-bold uppercase text-xs tracking-[0.2em]">
            {format(currentTime, "EEEE d 'de' MMMM", { locale: es })}
          </p>
        </div>

        <div className="space-y-8">
          <div className="text-center bg-slate-50 py-4 rounded-3xl border border-slate-100">
            <p className="text-slate-400 text-[10px] mb-1 uppercase font-black tracking-widest">Trabajador Activo</p>
            <p className="text-xl font-black text-slate-800">{user?.name || user?.email}</p>
            {isLocutor && (
              <p className="text-[10px] text-amber-500 font-black uppercase tracking-widest mt-1">📡 Locutor / Operaciones</p>
            )}
            {isGestor && (
              <p className="text-[10px] text-violet-500 font-black uppercase tracking-widest mt-1">💼 Gestor</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleMark('in')}
              disabled={loading || status === 'in'}
              className="flex flex-col items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-100 disabled:text-slate-300 text-white rounded-3xl py-8 transition-all shadow-lg shadow-emerald-200/50"
            >
              <LogIn size={32} />
              <span className="font-black uppercase text-xs tracking-widest">Iniciar Turno</span>
              {isLocutor && <span className="text-[9px] opacity-70 font-bold">→ Ir a Operaciones</span>}
            </button>

            <button
              onClick={() => handleMark('out')}
              disabled={loading || status === 'out'}
              className="flex flex-col items-center justify-center gap-3 bg-rose-500 hover:bg-rose-600 disabled:bg-slate-100 disabled:text-slate-300 text-white rounded-3xl py-8 transition-all shadow-lg shadow-rose-200/50"
            >
              <LogOut size={32} />
              <span className="font-black uppercase text-xs tracking-widest">Finalizar</span>
            </button>
          </div>

          {status === 'in' && (
            <div className="flex items-center justify-center gap-2 text-emerald-600 font-black text-[10px] uppercase tracking-widest animate-pulse">
              <Activity size={14} /> Turno en curso actualmente
            </div>
          )}
        </div>

        {message && (
          <div className={`mt-8 p-4 rounded-2xl text-center font-bold text-sm flex items-center justify-center gap-2 ${
            message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'
          }`}>
            {message.type === 'error' && <AlertCircle size={16} />}
            {message.text}
          </div>
        )}

        {/* Botones de Navegación/Salida */}
        <div className="mt-10 pt-8 border-t border-slate-100 flex flex-col gap-3">

          {/* Botón principal para locutor: Ir a Operaciones (cuando turno activo) */}
          {isLocutor && status === 'in' && (
            <button
              onClick={() => navigate('/transport/dispatch')}
              className="w-full flex items-center justify-center gap-2 py-4 bg-amber-500 text-white rounded-2xl font-bold uppercase text-xs tracking-widest hover:bg-amber-600 transition-colors shadow-lg shadow-amber-200"
            >
              <Radio size={16} /> Ir a Panel de Operaciones
            </button>
          )}

          {/* Botón principal para gestor: Ir al Dashboard (cuando turno activo) */}
          {isGestor && status === 'in' && (
            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center gap-2 py-4 bg-violet-600 text-white rounded-2xl font-bold uppercase text-xs tracking-widest hover:bg-violet-700 transition-colors shadow-lg shadow-violet-200"
            >
              <ArrowLeft size={16} /> Ir al Dashboard
            </button>
          )}

          <button
            onClick={() => navigate('/admin/asistencia')}
            className="w-full flex items-center justify-center gap-2 py-4 bg-blue-600 text-white rounded-2xl font-bold uppercase text-xs tracking-widest hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
          >
            <Calendar size={16} /> Ver mis Turnos
          </button>

          {(user?.role === 'admin' || user?.role === 'gestor' || user?.role === 'operaciones') && (
            <button
              onClick={() => {
                if (user?.role === 'operaciones') navigate('/admin/asistencia');
                else navigate('/');
              }}
              className="w-full flex items-center justify-center gap-2 py-4 bg-slate-800 text-white rounded-2xl font-bold uppercase text-xs tracking-widest hover:bg-slate-900 transition-colors shadow-lg shadow-slate-200"
            >
              <ArrowLeft size={16} /> {user?.role === 'operaciones' ? 'Volver a Gestión' : 'Volver al Dashboard'}
            </button>
          )}
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 py-4 bg-white text-rose-600 rounded-2xl font-bold uppercase text-xs tracking-widest hover:bg-rose-50 transition-colors border-2 border-rose-100"
          >
            <LogoutIcon size={16} /> Cerrar Sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkAttendance;
